import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../../api/auth';
import { FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';

const Container = styled.div`
  max-width: 500px;
  margin: 40px auto;
  padding: 30px;
  background-color: ${props => props.theme.card};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
`;

const Title = styled.h2`
  color: ${props => props.theme.text};
  margin-bottom: 20px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 15px;
  
  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.textSecondary};
    font-size: 18px;
  }
`;

const Input = styled.input`
  padding: 12px 40px 12px 40px;
  width: 100%;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 16px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  transition: all 0.2s;
  box-sizing: border-box;
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}33;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const Button = styled.button`
  padding: 12px;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin-top: 10px;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.primaryHover};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  margin-bottom: 15px;
  font-size: 14px;
  padding: 10px;
  background-color: ${props => props.theme.errorBg};
  border-radius: 4px;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.success};
  margin-bottom: 15px;
  font-size: 14px;
  padding: 10px;
  background-color: ${props => props.theme.successBg};
  border-radius: 4px;
`;

const Message = styled.p`
  margin-bottom: 20px;
  color: ${props => props.theme.text};
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  padding: 4px;
  display: flex;
  align-items: center;
  
  &:hover {
    color: ${props => props.theme.text};
  }
  
  svg {
    position: static;
    transform: none;
    left: auto;
    font-size: 16px;
  }
`;

const ResetPasswordForm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener token de la URL
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que token exista
    if (!token) {
      setError('Token de restablecimiento no válido o expirado');
      return;
    }
    
    // Validar contraseñas
    if (!password || !confirmPassword) {
      setError('Por favor, completa todos los campos');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await authService.resetPassword(token, password);
      
      if (response.success) {
        setSuccess(true);
        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(response.message || 'Error al restablecer la contraseña');
      }
    } catch (err) {
      if (err.response && err.response.status === 429) {
        // Manejar error de rate limiting
        setError(err.response.data.message || 'Demasiadas solicitudes. Por favor, intenta más tarde.');
      } else {
        setError(err.message || 'Error al conectar con el servidor');
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (!token) {
    return (
      <Container>
        <Title>Enlace no válido</Title>
        <ErrorMessage>El enlace de restablecimiento de contraseña no es válido o ha expirado.</ErrorMessage>
        <Button onClick={() => navigate('/')}>Volver al inicio</Button>
      </Container>
    );
  }
  
  if (success) {
    return (
      <Container>
        <Title>¡Contraseña restablecida!</Title>
        <SuccessMessage>
          Tu contraseña ha sido restablecida correctamente. 
          Serás redirigido al inicio en unos segundos...
        </SuccessMessage>
        <Button onClick={() => navigate('/')}>Ir al inicio</Button>
      </Container>
    );
  }
  
  return (
    <Container>
      <Title>Establece una nueva contraseña</Title>
      
      <Form onSubmit={handleSubmit}>
        <Message>Ingresa tu nueva contraseña para restablecer tu cuenta.</Message>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <InputWrapper>
          <FaLock />
          <Input 
            type={showPassword ? "text" : "password"}
            placeholder="Nueva contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <PasswordToggle
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
          >
            {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
          </PasswordToggle>
        </InputWrapper>
        
        <InputWrapper>
          <FaLock />
          <Input 
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirmar nueva contraseña"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
          <PasswordToggle
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
          >
            {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
          </PasswordToggle>
        </InputWrapper>
        
        <Button type="submit" disabled={loading}>
          {loading ? 'Restableciendo...' : 'Restablecer contraseña'}
        </Button>
      </Form>
    </Container>
  );
};

export default ResetPasswordForm; 